import cx from "classnames";
import { CoreLink } from "common/CoreLink";
import { Icon } from "common/Icon";
import { Result } from "common/search/Result";
import { ResultsGroup } from "common/search/ResultsGroup";
import { Popover } from "common/styleguide/Popover";
import { Distance } from "common/units";
import { Record } from "immutable";
import fetchJSON from "lib/fetchJSON";
import { featureNames } from "lib/localization";
import { MultiSearch } from "lib/multiSearch";
import { urls } from "lib/urls";
import { experimentEnabled } from "lib/utils";
import moment from "moment";
import React, { Fragment } from "react";
import { findDOMNode } from "react-dom";
import { FormattedMessage as T } from "react-intl";
import styles from "./GlobalSearch.scss";

export class GlobalSearch extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isFocused: false,
      inputLength: 0,
      search: "",
      initialLoad: true,
      results: Record({
        rides: null,
        routes: null,
        publicRides: null,
        publicRoutes: null,
        recentPinned: null,
      })(),
    };
    this._multiSearch = MultiSearch({
      userId: props.currentUser.id,
      onResults: this.onResults,
    });
  }

  onInputFocused = () => {
    this.setState({
      isFocused: true,
    });
    this._multiSearch(this.state.search);
    experimentEnabled("pinned_in_searchbar") && this.grabRecentPinned();
  };

  // grab up to two items that the user has pinned in the last week
  grabRecentPinned = () => {
    const { currentUser } = this.props;
    const LAST_WEEK = moment().subtract(1, "week").format('YYYY-MM-DD"');
    const RECENT_PINNED = `/users/${currentUser.id}/favorites.json?apikey=x&version=2&limit=1&order_by=created_at&order_dir=DESC&last_value=${LAST_WEEK}`;
    // bit of a bodge but we don't have a favorites reducer yet.
    fetchJSON(RECENT_PINNED).then((response) => this.onResults("recentPinned", response));
  };

  onChange = (e) => {
    const search = e.target.value;
    this._multiSearch(search);
    this.setState({ search, isFocused: true });
  };

  onResults = (resultsType, results) => {
    this.setState({
      results: this.state.results.set(resultsType, results),
      initialLoad: false,
    });
  };

  onClickOverlay = () => {
    if (document.activeElement === this._searchInput && document.hasFocus()) {
      return;
    }
    setTimeout(() => this.setState({ isFocused: false }), 0);
  };

  clear = () => {
    this._searchInput.blur();
    this.setState({ isFocused: false, search: "" });
  };

  onKeyUp = (e) => {
    const ESC = 27;
    if (e.keyCode === ESC) {
      this._searchInput.blur();
      this.setState({ isFocused: false });
    }
  };

  render() {
    const { search, results, initialLoad, isFocused } = this.state;
    const { intl, allowSearchExpand } = this.props;

    return (
      <div className={cx(styles.GlobalSearch, isFocused && styles.isFocused, !allowSearchExpand && styles.crimp)}>
        <input
          ref={(r) => {
            this._searchInput = r;
          }}
          onFocus={this.onInputFocused}
          className={styles.searchInput}
          type="text"
          value={search}
          onKeyUp={this.onKeyUp}
          onChange={this.onChange}
          placeholder={intl.formatMessage(featureNames.search)}
        />
        <Icon i="md-close" size={20} className={styles.clearIcon} onClick={this.clear} />
        {isFocused && !initialLoad && (
          <Popover
            noPadding
            intl={intl}
            container={window.body}
            target={() => findDOMNode(this)}
            placement="bottom"
            maxWidth="none"
            onRequestClose={this.onClickOverlay}
          >
            <SearchResults results={results} search={search} />
          </Popover>
        )}
      </div>
    );
  }
}

const NoResults = () => (
  <div className={styles.NoResults}>
    <Icon i="ios-map-outline" className={styles.emptyIcon} size={20} />
    <br />
    <T id="globalSearch.noResults" defaultMessage="No results found" />
  </div>
);

const Detail = ({ troute }) => (
  <Fragment>
    <Distance short value={troute.distance} /> · {troute.locality}, {troute.administrative_area}
  </Fragment>
);

const SearchResults = ({ results, search }) => {
  const hasResults = (searchType) => results[searchType] && results[searchType].results.length > 0;
  const noResults = !(hasResults("routes") || hasResults("rides") || hasResults("publicRoutes"));
  const showPinned = experimentEnabled("pinned_in_searchbar") && !search && hasResults("recentPinned");
  return (
    <div className={styles.SearchResults}>
      {showPinned && (
        <ResultsGroup title={<T id="globalSearch.recentlyPinned" defaultMessage="Recently Pinned" />}>
          {results.recentPinned.results.map((r) => {
            const id = r[r.type].id;
            return (
              <LinkResult
                key={id}
                img={`/${r.type}s/thumb/${id}.png`}
                url={urls[r.type + "s"].show(id)}
                title={r[r.type].name}
                detail={<Detail troute={r[r.type]} />}
              />
            );
          })}
        </ResultsGroup>
      )}
      {hasResults("routes") && (
        <ResultsGroup
          title={<T id="globalSearch.yourRoutes" defaultMessage="Your Routes" />}
          footer={<FindPageFooter number={results.routes.resultsCount} url={results.routes.urls.html} />}
        >
          {results.routes.results.map((r) => (
            <LinkResult
              key={r.route.id}
              img={`/routes/thumb/${r.route.id}.png`}
              url={urls.routes.show(r.route.id)}
              title={r.route.name}
              detail={<Detail troute={r.route} />}
            />
          ))}
        </ResultsGroup>
      )}
      {hasResults("rides") && (
        <ResultsGroup
          title={<T id="globalSearch.yourRides" defaultMessage="Your Rides" />}
          footer={<FindPageFooter number={results.rides.resultsCount} url={results.rides.urls.html} />}
        >
          {results.rides.results.map((r) => (
            <LinkResult
              key={r.trip.id}
              img={`/trips/thumb/${r.trip.id}.png`}
              url={urls.trips.show(r.trip.id)}
              title={r.trip.name}
              detail={<Detail troute={r.trip} />}
            />
          ))}
        </ResultsGroup>
      )}
      {search.length > 0 && hasResults("publicRoutes") && (
        <ResultsGroup
          title={<T id="globalSearch.publicRoutes" defaultMessage="Public Routes" />}
          footer={<FindPageFooter number={results.publicRoutes.resultsCount} url={results.publicRoutes.urls.html} />}
        >
          {results.publicRoutes.results.map((r) => (
            <LinkResult
              key={r.route.id}
              img={`/routes/thumb/${r.route.id}.png`}
              url={urls.routes.show(r.route.id)}
              title={r.route.name}
              detail={<Detail troute={r.route} />}
            />
          ))}
        </ResultsGroup>
      )}
      {noResults && <NoResults />}
    </div>
  );
};

const LinkResult = ({ url, ...props }) => (
  <CoreLink to={url} href={url} className={styles.LinkResult}>
    <Result {...props} />
  </CoreLink>
);

const FindPageFooter = ({ number, url }) => (
  <a href={url}>
    <T id="globalSearch.viewResults" defaultMessage="View {number} Results" values={{ number }} />
  </a>
);
